import { Money } from '@palaccount/money';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { useSupplierItemHistory } from '@/features/purchases/returns-api';
import { useItemHistory } from '@/features/sales/returns-api';

import type { LineSide } from './line-tools';

/**
 * Wave 4C — "Last price {amount} on {date}" under a line's item, with a
 * one-click "Use".
 *
 * Sales grids (invoice, order) read the CUSTOMER's last posted invoice price
 * for the item (`/partners/:id/item-history`); purchasing grids (bill, PO)
 * read the SUPPLIER's last posted bill price (`/partners/:id/supplier-item-
 * history`). It is a hint, never a fill: the line's price is whatever the
 * pick or the clerk put there until "Use" is clicked.
 *
 * Shown only when the last price is in the document's currency — a price in
 * another currency is a different number, and converting it here would be a
 * guess at the rate. Nothing is drawn while the history loads, or when there
 * is none: an absent hint, never a false one.
 */
export function LastPriceHint({
  side,
  partnerId,
  itemId,
  currency,
  currentPrice,
  editable,
  line,
  onUse,
}: {
  side: LineSide;
  partnerId: string;
  itemId: string;
  currency: string;
  /** The line's price now; "Use" hides when it already equals the last price. */
  currentPrice: string;
  editable: boolean;
  /** 1-based line number, for the button's accessible name. */
  line: number;
  onUse: (price: string) => void;
}) {
  const { t } = useTranslation();
  const sales = useItemHistory(side === 'sales' ? partnerId || undefined : undefined, itemId || undefined);
  const purchase = useSupplierItemHistory(
    side === 'purchase' ? partnerId || undefined : undefined,
    itemId || undefined,
  );
  const history = side === 'sales' ? sales.data : purchase.data;
  if (!history || history.lastPrice === null || history.lastPriceAt === null) return null;
  if (history.lastPriceCurrency !== currency) return null;
  const lastPrice = history.lastPrice;
  const same = samePrice(currentPrice, lastPrice, currency);

  return (
    <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      <span>
        {t('lines.lastPrice')} <Amount value={lastPrice} currency={currency} /> {t('lines.lastPriceOn')}{' '}
        <Ltr value={history.lastPriceAt} />
      </span>
      {editable && !same ? (
        <button
          type="button"
          className="font-medium text-primary underline underline-offset-2"
          aria-label={t('lines.useLastPrice', { line })}
          onClick={() => onUse(lastPrice)}
        >
          {t('lines.use')}
        </button>
      ) : null}
    </p>
  );
}

/** Decimal comparison through Money ("9.9" and "9.90" are one price); a half-typed box is never "the same". */
function samePrice(current: string, last: string, currency: string): boolean {
  try {
    return current.trim() !== '' && Money.of(current.trim(), currency).equals(Money.of(last, currency));
  } catch {
    return false;
  }
}
